import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Headphones, Footprints, Eye } from "lucide-react";
import useSeo from "../hooks/useSeo";
import { PublicHeader } from "../components/PublicHeader";
import { GuestReturnBanner } from "../components/GuestReturnBanner";
import { PromoScarcityBanner } from "../components/PromoScarcityBanner";
import { RotatingQuotes } from "../components/RotatingQuotes";
import { secureRandom } from "../lib/secureRandom";

// Public-facing marketing homepage. Inspired by the bilateral-wellness
// reference site. Sits at "/" and is shown to ANY visitor — logged-in users
// get auto-redirected to the dashboard so the landing only runs for
// new/anonymous traffic.

const MarqueeWords = () => {
  // Source list — randomised once per mount so visitors see a different
  // ordering on every page load (still consistent during a session).
  const wordPool = [
    "breathe", "return", "soften", "regulate", "connect", "listen",
    "arrive", "ground", "open", "rest", "notice",
    "heal", "thrive", "adapt", "restore", "mend", "rise", "rebalance",
    "slow down",
  ];
  // Fisher-Yates shuffle (memoised so the array is stable for this render).
  const words = useMemo(() => {
    const a = [...wordPool];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(secureRandom() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Two copies side-by-side keeps the loop seamless.
  const repeated = [...words, ...words];
  return (
    <div
      className="overflow-hidden border-y border-[#E5DCC9]/50 bg-[#FBF6EE]/60"
      data-testid="home-marquee"
    >
      <div className="flex gap-10 py-6 whitespace-nowrap animate-marquee">
        {repeated.map((w, i) => (
          <span
            key={`${w}-${i}`}
            className="font-outfit italic text-2xl sm:text-3xl text-[#3090A8]/80 lowercase tracking-wide"
          >
            {w}
            <span className="ml-10 text-[#E2B887]">·</span>
          </span>
        ))}
      </div>
    </div>
  );
};

const ChannelCard = ({ Icon, title, body, tint }) => (
  <div
    className="bg-white rounded-3xl p-7 border border-slate-100 shadow-[0_4px_24px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_36px_rgba(0,0,0,0.08)] transition-shadow duration-300"
    data-testid={`channel-card-${title.toLowerCase()}`}
  >
    <div
      className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
      style={{ backgroundColor: `${tint}22` }}
    >
      <Icon className="w-7 h-7" style={{ color: tint }} />
    </div>
    <h3 className="font-outfit font-semibold text-xl text-[#1B2349] mb-2">{title}</h3>
    <p className="text-[#4A5568] leading-relaxed">{body}</p>
  </div>
);

const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  useSeo({
    title: "EMDR Bilateral Beats | Your Brain Balance Reset",
    description:
      "Calm anxiety, ease insomnia, and soothe stress and trauma with bilateral stimulation and bilateral sound — inspired by EMDR (Eye Movement Desensitization and Reprocessing). Guided 9-step practices, breath cues, and a daily audio library. Start your 5-day free trial.",
    path: "/",
  });

  // Logged-in subscribers go straight to the app.
  useEffect(() => {
    if (user) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-[#FBF8F1]" data-testid="home-page">
      <PublicHeader />
      <GuestReturnBanner />

      {/* Hero — reeds-against-sky photographic backdrop, layered with a warm
          cream gradient on the left so the headline + paragraph stay legible. */}
      <section
        className="relative overflow-hidden"
        data-testid="home-hero"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/images/home-hero-reeds.webp')" }}
        />
        {/* Cream wash that fades from solid on the left to transparent on the
            right — keeps text readable while letting the imagery breathe.
            The wash is warmer / softer for the gold meadow imagery. */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(95deg, rgba(251,248,241,0.92) 0%, rgba(251,248,241,0.78) 35%, rgba(251,248,241,0.30) 70%, rgba(251,248,241,0.00) 100%)",
          }}
        />

        <div className="relative px-6 sm:px-10 pt-8 pb-16 max-w-7xl mx-auto">
          <div className="mb-6" id="promo-full-banner">
            <PromoScarcityBanner variant="hero" />
          </div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#3090A8] font-medium mb-6">
            Bilateral Stimulation · Self-Help App
          </p>
          <h1 className="font-outfit font-semibold text-[2.75rem] leading-[1.05] sm:text-6xl lg:text-7xl text-[#1B2349] tracking-tight max-w-4xl">
            An easier, kinder way to{" "}
            <em className="italic font-extrabold text-[#3090A8]">regulate</em>{" "}
            your nervous system.
          </h1>
          <p className="mt-7 text-lg sm:text-xl text-[#4A5568] leading-relaxed max-w-2xl">
            Bilateral Calm uses gentle left-right audio practices — the same
            principles therapists use in EMDR-informed work — so you can
            self-soothe in minutes, anywhere.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <button
              onClick={() =>
                navigate("/intervention/dial_down_tension?guest=1")
              }
              className="px-7 py-3.5 rounded-full bg-[#1B2349] text-white font-medium hover:bg-[#0F1838] transition-all duration-200 active:scale-[0.98] shadow-sm"
              data-testid="hero-try-guest-btn"
            >
              Try it free — no account needed
            </button>
            <button
              onClick={() => navigate("/pricing")}
              className="px-7 py-3.5 rounded-full bg-white border border-[#1B2349]/15 text-[#1B2349] font-medium hover:border-[#1B2349]/40 hover:bg-[#FBF6EE] transition-all duration-200 active:scale-[0.98]"
              data-testid="hero-see-trial-btn"
            >
              See how the trial works
            </button>
            <button
              onClick={() => navigate("/login")}
              className="px-7 py-3.5 rounded-full bg-white border border-[#1B2349]/15 text-[#1B2349] font-medium hover:border-[#1B2349]/40 hover:bg-[#FBF6EE] transition-all duration-200 active:scale-[0.98]"
              data-testid="hero-learn-more-btn"
            >
              Learn more
            </button>
          </div>
          <p className="mt-5 text-sm text-[#1B2349]">
            Sample the first four tasks free — then start a 5-day starter trial or unlock everything. Cancel anytime.
          </p>
        </div>
      </section>

      <MarqueeWords />

      {/* How it works */}
      <section className="px-6 sm:px-10 py-20 max-w-7xl mx-auto">
        <p className="text-xs uppercase tracking-[0.2em] text-[#3090A8] font-medium mb-4">
          How it works
        </p>
        <h2 className="font-outfit font-semibold text-3xl sm:text-4xl lg:text-5xl text-[#1B2349] tracking-tight max-w-3xl mb-5">
          Three simple channels the brain already knows.
        </h2>
        <p className="text-lg text-[#4A5568] leading-relaxed max-w-2xl mb-12">
          Bilateral stimulation — alternating left-right input — can gently
          down-regulate a reactive system. Choose the channel that fits your
          moment.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <ChannelCard
            Icon={Headphones}
            title="Audio"
            body="Stereo-panning tones, bells, pulses, and singing bowls — made for headphones."
            tint="#3090A8"
          />
          <ChannelCard
            Icon={Eye}
            title="Eye movement"
            body="Each session guides slow left-right eye motion in time with the bilateral sound."
            tint="#78C0D8"
          />
          <ChannelCard
            Icon={Footprints}
            title="Tapping"
            body="Built-in butterfly hugs and leg-tap rhythm cues to ground you when you can't listen."
            tint="#E2B887"
          />
        </div>
      </section>

      {/* Who it helps — dense, SEO-friendly content section. Prose paragraphs
          describe the emotional/physical challenges the app addresses, using
          the target keyword set (bilateral stimulation, EMDR, anxiety,
          insomnia, stress, trauma). Improves text-to-HTML ratio and gives
          Google real substance to rank. */}
      <section
        className="px-6 sm:px-10 py-24 max-w-5xl mx-auto"
        data-testid="home-who-it-helps"
      >
        <p className="font-outfit text-xs uppercase tracking-[0.2em] text-[#6B8668] font-semibold mb-4">
          Who it helps
        </p>
        <h2 className="font-outfit font-semibold text-3xl sm:text-4xl lg:text-5xl text-[#1B2349] tracking-tight mb-10 max-w-3xl">
          Built for the anxious, the sleep-deprived, and the quietly stuck.
        </h2>

        <div className="grid md:grid-cols-2 gap-10 lg:gap-14 text-[#4A5568] leading-relaxed">
          <div>
            <h3 className="font-outfit font-semibold text-xl text-[#1B2349] mb-3">
              For chronic anxiety and racing thoughts
            </h3>
            <p>
              Bilateral Calm's guided practices for anxiety pair panned
              left-right audio with a guided flow — to settle a nervous
              system stuck in overdrive. Sessions shift anxiety from a
              spiral to a steady state... faster than talking yourself out
              of it. If panic attacks, morning dread, or work anxiety are
              running your day, this is for you.
            </p>
          </div>

          <div>
            <h3 className="font-outfit font-semibold text-xl text-[#1B2349] mb-3">
              For insomnia and fear of sleeplessness
            </h3>
            <p>
              The sleep-anxiety practice is a favorite of insomniacs. It
              gently names the fear of not-sleeping — the loop that keeps
              you awake once you notice you're still awake — while the deep,
              slow bilateral audio drops your body into a parasympathetic
              state. Play it before you sleep, follow the breath cues, and
              let the left-right rhythm wash away your worry. People with
              decades of poor sleep can finally fall asleep on night one.
            </p>
          </div>

          <div>
            <h3 className="font-outfit font-semibold text-xl text-[#1B2349] mb-3">
              For stress, overwhelm, and body tension
            </h3>
            <p>
              Bilateral stimulation isn't only for the acutely anxious — it's
              a daily nervous-system tune-up. Using the app after a rough
              meeting, an argument with your partner, or a long caregiving
              day can measurably lower shoulder tension, headache pressure,
              and jaw clenching. Think of it as brushing your teeth for your
              emotional system: light, consistent, genuinely restorative.
            </p>
          </div>

          <div>
            <h3 className="font-outfit font-semibold text-xl text-[#1B2349] mb-3">
              For stage fright and performance pressure
            </h3>
            <p>
              Musicians, athletes, public speakers, and interviewees use the
              stage-fright practice before they take the microphone. It
              borrows directly from EMDR resource-installation: imagine the
              performance going well, hold that image steady, and let the
              bilateral tones pair it with a calmed body. The nerves will
              settle — and you will meet the challenge from a grounded
              place instead of a flooded one.
            </p>
          </div>

          <div>
            <h3 className="font-outfit font-semibold text-xl text-[#1B2349] mb-3">
              For grief, anger, and healing old stories
            </h3>
            <p>
              Some sessions are gentler and slower — designed for the times
              you feel raw. Guided practices for grief, anger, and
              self-worth walk you through structured reflection paired with
              soft bilateral audio, giving hard emotions an offramp instead
              of asking you to push them away. Bilateral Calm is not therapy
              and does not replace working with a licensed clinician on
              trauma, but it complements that work beautifully between
              sessions.
            </p>
          </div>

          <div>
            <h3 className="font-outfit font-semibold text-xl text-[#1B2349] mb-3">
              For building new habits and holding boundaries
            </h3>
            <p>
              A rarer category — but a powerful one. Practices for habit
              building and boundary holding use bilateral stimulation to
              pair a chosen intention with a settled body state, so the new
              behavior has a felt sense attached to it rather than just a
              mental note. Members use these to quit doomscrolling, let go
              of vices, protect their mornings, and finally say no to plans
              they don't want.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ preview — three condensed answers with a link out to the full
          FAQ page. Adds long-tail keyword coverage on the home page while
          keeping the layout clean. */}
      <section
        className="px-6 sm:px-10 py-24 max-w-4xl mx-auto"
        data-testid="home-faq-preview"
      >
        <p className="font-outfit text-xs uppercase tracking-[0.2em] text-[#6B8668] font-semibold mb-4">
          Common questions
        </p>
        <h2 className="font-outfit font-semibold text-3xl sm:text-4xl lg:text-5xl text-[#1B2349] tracking-tight mb-10">
          What most people ask before starting.
        </h2>

        <div className="space-y-8 text-[#4A5568] leading-relaxed">
          <div>
            <h3 className="font-outfit font-semibold text-lg sm:text-xl text-[#1B2349] mb-2">
              Is Bilateral Calm the same as EMDR therapy?
            </h3>
            <p>
              No. EMDR (Eye Movement Desensitization and Reprocessing) is a
              clinical trauma therapy delivered by a licensed provider.
              Bilateral Calm is a self-help tool inspired by EMDR
              principles — specifically the alternating left-right
              stimulation at the heart of the therapy. It's designed for
              daily nervous-system regulation and everyday emotional
              challenges. It is not a substitute for professional care of
              PTSD, complex trauma, or serious mental health conditions.
            </p>
          </div>

          <div>
            <h3 className="font-outfit font-semibold text-lg sm:text-xl text-[#1B2349] mb-2">
              How does bilateral stimulation actually work?
            </h3>
            <p>
              Bilateral stimulation, audio, eye movement, or body tapping,
              that alternates between your left and right sides with a
              deliberate speed. When you move your eyes side to side, tap
              your thighs side to side, or listen through headphones, the
              alternating pattern engages both hemispheres of the brain and
              prompts a shift in your autonomic nervous system — heart rate
              settles, breathing slows, and the tight, activated state that
              anxiety produces starts to release. It's the same active
              ingredient that EMDR therapists use, translated into a form
              you can carry with you.
            </p>
          </div>

          <div>
            <h3 className="font-outfit font-semibold text-lg sm:text-xl text-[#1B2349] mb-2">
              Do I need headphones?
            </h3>
            <p>
              No. You can get excellent results simply by tapping or
              moving your eyes to the timing of the bilateral sound.
              If you wish to use bilateral sound sessions without
              tapping or eye movements — yes, headphones or earbuds
              are essential. The whole effect depends on the audio
              panning distinctly into one ear then the other. Any pair
              works; you don't need noise-cancelling. The Bilateral
              Calm app supports any or all of these modalities!
            </p>
          </div>
        </div>

        <div className="mt-10">
          <a
            href="/faq"
            className="inline-flex items-center gap-2 font-outfit font-semibold text-[#3090A8] hover:text-[#267B90] transition-colors"
            data-testid="home-faq-preview-link"
          >
            See all frequently asked questions →
          </a>
        </div>
      </section>

      {/* Testimonial — admin-managed rotating quotes (cross-fade every 4s).
          Falls back to nothing if the collection is empty; the section
          simply shrinks. Manage via /admin → "Quotes" tab. */}
      <section className="px-6 sm:px-10 py-24 max-w-4xl mx-auto">
        <RotatingQuotes />
      </section>

      {/* Final CTA — wrapped in a pill-shaped sage-tinted container to
          visually break the page into its own resting moment. */}
      <section className="px-6 sm:px-10 py-16 max-w-5xl mx-auto">
        <div
          className="rounded-[3rem] bg-[#3090A8]/15 border border-[#3090A8]/25 px-8 sm:px-14 py-16 sm:py-20 text-center"
          data-testid="home-final-cta-pill"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-[#6B8668] font-semibold mb-5">
            Ready when you are
          </p>
          <h2 className="font-outfit font-semibold text-3xl sm:text-4xl lg:text-5xl text-[#1B2349] tracking-tight mb-5">
            Begin with 5 free days. Keep going at $5.83 a month.
          </h2>
          <p className="text-lg text-[#4A5568] leading-relaxed mb-10 max-w-2xl mx-auto">
            Use Bilateral Calm on the web or on your phone. Your practices
            sync. Your progress follows you.
          </p>
          <button
            onClick={() => navigate("/pricing")}
            className="px-8 py-4 rounded-full bg-[#1B2349] text-white font-medium hover:bg-[#1A202C] transition-colors duration-200"
            data-testid="cta-start-signup-btn"
          >
            Start sign up
          </button>
          <p className="mt-5 text-sm text-[#718096]">
            Web + iOS + Android · Cancel anytime from your account.
          </p>
        </div>
      </section>

      {/* About block — required for App Store ownership-transfer paperwork.
          Publicly names the operating LLC and the contact email so an
          Apple reviewer visiting the site can verify domain ↔ business
          without hunting through legal pages. Kept short and calm. */}
      <section
        className="px-6 sm:px-10 py-16 max-w-3xl mx-auto"
        data-testid="about-mood-and-sleep-clinic"
        id="about"
      >
        <div className="border-t border-slate-200 pt-10">
          <h2 className="font-outfit font-semibold text-2xl text-[#1B2349] mb-3">
            About Bilateral Calm
          </h2>
          <p className="text-[#4A5568] leading-relaxed">
            Bilateral Calm is a product of{" "}
            <strong className="text-[#1B2349]">Mood and Sleep Clinic LLC</strong>, a company
            dedicated to accessible, evidence-informed practices for anxiety, insomnia, stress,
            and trauma recovery. The practices in this app are inspired by the same alternating
            left-right principles behind EMDR (Eye Movement Desensitization and Reprocessing).
          </p>
          <p className="text-[#4A5568] leading-relaxed mt-4">
            Questions or feedback — email{" "}
            <a
              href="mailto:mscwellness@protonmail.com"
              className="text-[#3090A8] underline underline-offset-2"
              data-testid="about-contact-email"
            >
              mscwellness@protonmail.com
            </a>
            .
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 sm:px-10 py-10 max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-[#718096]">
        <button onClick={() => navigate("/")} className="flex items-center" data-testid="footer-home-btn">
          <img src="/logo-bilateral-calm.png" alt="Bilateral Calm" className="h-20 sm:h-24 w-auto" />
        </button>
        <div className="flex gap-6">
          <button onClick={() => navigate("/about")} className="hover:text-[#1B2349]" data-testid="footer-about-btn">About</button>
          <button onClick={() => navigate("/practices")} className="hover:text-[#1B2349]" data-testid="footer-practices-btn">Practice Library</button>
          <button onClick={() => navigate("/pricing")} className="hover:text-[#1B2349]" data-testid="footer-pricing-btn">Plans</button>
          <button onClick={() => navigate("/faq")} className="hover:text-[#1B2349]" data-testid="footer-faq-btn">FAQ</button>
          <button onClick={() => navigate("/privacy")} className="hover:text-[#1B2349]" data-testid="footer-privacy-btn">Privacy</button>
          <button onClick={() => navigate("/security")} className="hover:text-[#1B2349]" data-testid="footer-security-btn">Security</button>
          <button onClick={() => navigate("/sign-in")} className="hover:text-[#1B2349]" data-testid="footer-signin-btn">Sign in</button>
        </div>
        <p data-testid="footer-legal">© {new Date().getFullYear()} Mood and Sleep Clinic LLC · Bilateral Calm™ · <a href="mailto:mscwellness@protonmail.com" className="underline underline-offset-2 hover:text-[#1B2349]">mscwellness@protonmail.com</a></p>
      </footer>
    </div>
  );
};

export default HomePage;
